import { connect } from 'react-redux';
import { routeTo } from 'redux-router-kit';

import * as AppDetails from 'app/entities/AppDetails';
import * as CliApp from 'app/entities/CliApp';
import * as DeveloperActions from 'app/developer-v3/Actions';
import * as PartnerProfile from 'app/entities/PartnerProfile';
import { Profile } from '@zapier/shared-entities';
import DashboardPage from 'app/developer-v3/pages/dashboard/DashboardPage';
import LoadingBlockOrComponent from 'app/common/components/LoadingBlockOrComponent';
import hocify from 'app/common/components/hocify';
import {
  getAppUrl,
  getAppVersionUrl,
  parseBackendAppToAppType,
  sortAppsByStatus,
} from 'app/developer-v3/utils';

import type { SemVer } from 'app/developer-v3/types/version';
import {
  getAppsPendingDeletion,
  getCategorizedAppEntitiesPendingDeletion,
} from 'app/developer-v3/utils/appsPendingDeletion';

const mapStateToProps = (state, props) => {
  const [appsPendingDeletion] = getCategorizedAppEntitiesPendingDeletion();

  const apps = [
    ...CliApp.selectors.all.entitiesData(state),
    ...appsPendingDeletion,
  ].map(parseBackendAppToAppType({ isLegacy: false }));

  const { id: integrationId } = props.params;
  const currentProfile = Profile.selectors.all.currentProfile(state);
  const partnerProfile = PartnerProfile.selectors.all.first(state);
  const isLoading =
    CliApp.selectors.all.isLoading(state) ||
    (integrationId ? AppDetails.selectors.all.isLoading(state) : false);

  /**
   * Checks if the API returned a status code of 5xx
   *
   * Used to show the user an error explaining that zapier.com API can be down
   */
  const hasFailure = CliApp.selectors.all.isFailure(state);

  return {
    apps: sortAppsByStatus(apps),
    isAdminMasqueraded: Profile.selectors.all.isMasquerading(state),
    isDeveloper: Profile.selectors.isDeveloper(currentProfile),
    isLoading,
    isLoadingPartnerProfile: PartnerProfile.selectors.all.isLoading(state),
    integrationId,
    hasAcceptedTerms: Profile.selectors.hasAcceptedTerms(currentProfile),
    hasPartnerProfile: PartnerProfile.selectors.hasPartnerProfile(
      partnerProfile
    ),
    hasPartnerRoles: PartnerProfile.selectors.hasPartnerRoles(partnerProfile),
    hasFailure,
  };
};

type Props = {
  params: {
    id?: string;
  };
};

const mapDispatchToProps = (dispatch, props: Props) => {
  const { id: inviteAppId } = props.params;
  return {
    onLoad() {
      dispatch(CliApp.actions.loadCollectionWithParams({ full: true }));
      dispatch(PartnerProfile.actions.loadCollection());
      if (inviteAppId) {
        dispatch(AppDetails.actions.loadEntity(null, `cli/${inviteAppId}`));
      }
    },
    async onView({
      id,
      version,
      isBeta,
      isPublic,
    }: {
      id: number;
      version: SemVer;
      isBeta: boolean;
      isPublic: boolean;
    }) {
      const url =
        !isPublic || isBeta
          ? getAppVersionUrl(id, version)
          : getAppUrl(id, false);
      dispatch(routeTo(url));
    },
    async onEdit({ id, version }: { id: number; version: SemVer }) {
      dispatch(routeTo(getAppVersionUrl(id, version)));
    },
    async onDelete({
      id,
      version,
      isLegacy,
    }: {
      id: number;
      version?: string;
      isLegacy: boolean;
    }) {
      if (!version) {
        await dispatch(DeveloperActions.deleteApp(id, isLegacy));

        const apps = getAppsPendingDeletion();
        if (apps[id]) {
          return { isPendingDeletion: true };
        }
      } else {
        await dispatch(DeveloperActions.deleteAppVersion(id, version));
      }
      await dispatch(CliApp.actions.loadCollectionWithParams({ full: true }));

      return undefined;
    },
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(hocify(LoadingBlockOrComponent)(DashboardPage));
